import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Logger } from '../../util/logger/logger.service';
import { presentLoader } from '../../util/ion-util';
import { UserService } from '../../services/user.service';
import { IonInput, LoadingController, NavController, Platform } from '@ionic/angular';
import { SegmentCMOSService } from 'src/app/services/segment-cmos.service';
import { getApplicationHomeQueryParamString } from 'src/app/util/job-util';
import { IntercomService } from 'src/app/services/intercom.service';
import { CodeInputComponent } from 'angular-code-input';
import { getMfaText } from './common';

@Component({
    selector: 'app-signin',
    templateUrl: './signin.component.html',
    styleUrls: ['./signin.component.scss']
})
export class SigninComponent implements OnInit {

    public error: any;
    public passwordResetRequired: boolean;
    public mfaEnabled: boolean = false;
    public mfaText: string = "";

    form: FormGroup;

    showPassword = false;

    @ViewChild('passwordInput')
    private passwordInput: IonInput;

    @ViewChild('codeInput')
    private codeInput: CodeInputComponent;

    constructor(private formBuilder: FormBuilder,
                private navCtrl: NavController,
                private userService: UserService,
                private logger: Logger,
                private loadingCtrl: LoadingController,
                private platform: Platform,
                private segmentCMOS: SegmentCMOSService,
                private intercomService: IntercomService,
                ) {
    }

    ngOnInit() {
        this.form = this.formBuilder.group({
          username: new FormControl<string>('', [Validators.compose([Validators.required, Validators.email, Validators.pattern(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)])]),
          password: new FormControl<string>('', Validators.compose([Validators.required, Validators.minLength(6)]))
        },
        { updateOn: "blur"}
        );
    }

    $msg: string;
    async onSubmit() {
      this.error = undefined;
      this.passwordResetRequired = undefined;

      if (this.form.invalid) {
          this.$msg = 'All form fields are required.';
          return this.$msg;
      }
    
      // make sure email is lowercase
      this.form.controls.username.setValue(this.form.value.username.toLowerCase());

      const loader = await presentLoader(this.loadingCtrl);

      this.userService.login({ username: this.form.value.username, password: this.form.value.password })
        .subscribe({
          next: async () => {
            await loader.dismiss();
            await this.userService.ready();
            
            if (this.userService.currentCodeDeliveryDestination) {
              this.mfaEnabled = true;
              this.mfaText = getMfaText(this.userService.currentCodeDeliveryDestination);
              return;
            }
            

            await this.finishLogin();
          },
          error: async error => {
            await loader.dismiss();
            this.segmentCMOS.addEvent('signin_failure', {
              'message': error.message,
              'username': this.form.value.username
            });
            this.logger.error(error);
            if (error.message.includes('Password reset required')) {
              this.passwordResetRequired = true;
            } else {
              this.error = error.message;
            }
          }
        });
    }

    async finishLogin() {
      // Passing in empty string so error does not show in courier UI
      let segmentProps = {cancellation_token : ""}

      if (this.platform.is('hybrid')) {
        segmentProps.cancellation_token = `websignup_${this.userService.currentUser.userInfo.externalId}`
      }

      this.segmentCMOS.addEvent('signin_success', segmentProps);
      const jobId = await this.userService.getJobId();
      const jobIds = await this.userService.getJobIds();
      if (jobIds && this.userService.currentUser.userInfo.phoneVerified) {
        await this.userService.setJobIds(null);
        await this.navCtrl.navigateRoot(`/application-home${getApplicationHomeQueryParamString(jobIds)}`);
      } else if (jobId && this.userService.currentUser.userInfo.phoneVerified) {
        await this.userService.setJobId(null);
        await this.navCtrl.navigateRoot(`/application-home?externalId=${jobId}`);
      } else {
        const url = this.userService.redirectUrl ?? '/';
        this.userService.redirectUrl = undefined;  // consume the redirect url
        this.navCtrl.navigateRoot(url);
      }
    }

    toggleShowPassword() {
        this.showPassword = !this.showPassword;
        this.passwordInput.type = this.showPassword ? 'text' : 'password';
    }

    onOpenIntercom() {
        this.intercomService.showNewMessage();
    }

    onCodeChanged(event: any) { }

    // this called only if user entered full code
    async onCodeCompleted(code: string) {
      const loader = await presentLoader(this.loadingCtrl);
      this.userService.confirmLogin(code)
        .subscribe({
          next: async () => {
            await this.finishLogin();
            await loader.dismiss();
          },
          error: async error => {
            this.error = error;
            await loader.dismiss();
            this.codeInput.reset();
            this.logger.error(error);
            throw error;
          }
      })
    }
}
